/* eslint-disable react-hooks/exhaustive-deps */
'use client';
import { useState, useMemo, useCallback, useEffect } from 'react';
import {
  Box,
  Paper,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select as MuiSelect,
  OutlinedInput,
  CircularProgress,
  Typography,
  Stack,
  LinearProgress,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  TablePagination,
  TextField,
  InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DownloadIcon from '@mui/icons-material/CloudDownloadOutlined';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import Menu from '@mui/material/Menu';
import ExcelJS from 'exceljs';
import Papa from 'papaparse';
import { useMasterVehicleList } from '../../stores/masterVehicleStore';
import { filterReportFuelSummary } from './constant';
import { timezone } from '../../utils/timeZone';
import { useLanguage } from '../../utils/i18n';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useNotifier } from '../../components/Notifier';
import { useCompanyDataList } from '../../stores/companyStore';
import PickerField from '../../components/PickerField';
import usePickerPagination from '../../hooks/usePickerPagination';
import { useNavigate } from 'react-router-dom';

const getCompanyIdFromStorage = () => {
  try {
    if (typeof window !== 'undefined' && window.localStorage) {
      const stored = localStorage.getItem('companyId');
      if (stored && String(stored).toLowerCase() !== 'all') {
        return String(stored);
      }
    }
  } catch (err) {
    console.warn('cannot read localStorage', err);
  }
  return '';
};

function VehicleFuelSummaryPageContent() {
  const { t } = useLanguage();
  const notify = useNotifier();
  const [rows, setRows] = useState([]);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingText, setLoadingText] = useState('');

  // Pagination state for custom HTML Table
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // Filter states
  const [periodType, setPeriodType] = useState('today');
  const [fromDate, setFromDate] = useState('');
  const [toDate, setToDate] = useState('');
  const [dateError, setDateError] = useState('');

  // Top Search states
  const [searchField, setSearchField] = useState('all');
  const [tableSearchQuery, setTableSearchQuery] = useState('');

  useEffect(() => {
    if (!periodType) return;
    if (periodType === 'custom' && fromDate && toDate) return;

    const now = dayjs();
    if (periodType === 'custom') {
      if (!fromDate) {
        setFromDate('');
        setToDate('');
      }
      return;
    }

    let from;
    let to = now;

    if (periodType === 'today') {
      from = now.startOf('day');
      to = now.second(59);
    } else if (periodType === 'week' || periodType === 'last_week') {
      from = now.subtract(7, 'day').startOf('day');
      to = now.endOf('day');
    } else if (periodType === 'month' || periodType === 'last_month') {
      from = now.subtract(1, 'month').startOf('day');
      to = now.endOf('day');
    } else {
      return;
    }

    if (from && to) {
      setFromDate(from.format('YYYY-MM-DD HH:mm:ss'));
      setToDate(to.format('YYYY-MM-DD HH:mm:ss'));
    }
  }, [periodType]);

  const [selectedCompanyIds, setSelectedCompanyIds] = useState(() => {
    const defaultId = getCompanyIdFromStorage();
    return defaultId ? [defaultId] : [];
  });

  const companyPickerParams = usePickerPagination({
    fetchHook: useCompanyDataList,
    buildArgs: (state) => [state.page, state.pageSize, state.searchKey, state.searchValue],
    searchKey: 'name'
  });
  const companyOptions = companyPickerParams.options;

  const selectedCompaniesParam = useMemo(() => {
    if (selectedCompanyIds.length === 0 || selectedCompanyIds.includes('all')) {
      return companyOptions.map((c) => ({ value: c.value }));
    }
    return selectedCompanyIds.map((id) => ({ value: id }));
  }, [selectedCompanyIds, companyOptions]);

  const validateDateRange = (start, end) => {
    if (!start || !end) return false;
    const diffMinutes = end.diff(start, 'minute');
    if (diffMinutes > 31 * 24 * 60) {
      setDateError(t('vehicleFuelSummary.errMaxRange31Days', 'Maximum date range is 31 days'));
      return false;
    } else if (diffMinutes < 0) {
      setDateError(t('vehicleFuelSummary.errStartTimeLater', 'Start time cannot be later than end time'));
      return false;
    }
    setDateError('');
    return true;
  };

  const vehiclePickerParams = usePickerPagination({
    fetchHook: useMasterVehicleList,
    buildArgs: (state) => [state.page, state.pageSize, state.companyId, state.searchKey, state.searchValue],
    formatOption: (p) => ({
      value: p.id,
      label: p.license_plate,
      original: p
    }),
    searchKey: 'license_plate',
    companyId: selectedCompaniesParam
  });
  const vehicleList = vehiclePickerParams.options;

  const vehicleOptionsNormalized = useMemo(
    () =>
      (vehicleList || []).map((c) => ({
        value: String(c.id ?? c.vehicle_id ?? c.value ?? c),
        label: c.original?.license_plate ?? c.license_plate ?? c.label ?? c.name ?? String(c.id ?? ''),
        companyName:
          c.original?.join?.company_name ||
          c.original?.company_name ||
          c.join?.company_name ||
          c.company_name ||
          c.companyName ||
          'Unknown Company',
        raw: c.raw ?? c
      })),
    [vehicleList]
  );

  const [selectedVehicles, setSelectedVehicles] = useState([]);
  const selectedVehicleIds = useMemo(() => {
    return selectedVehicles.map((v) => String(v.value));
  }, [selectedVehicles]);

  const handleVehicleChange = (ids) => {
    const newSelected = ids.map((id) => {
      const strId = String(id);
      const match = vehicleOptionsNormalized.find((v) => String(v.value) === strId);
      if (match) return match;
      const prevMatch = selectedVehicles.find((v) => String(v.value) === strId);
      if (prevMatch) return prevMatch;

      const rawMatch =
        (vehicleList || []).find((v) => String(v.value) === strId) ||
        (vehiclePickerParams.listOptions || []).find((v) => String(v.value) === strId);
      if (rawMatch) {
        return {
          value: strId,
          label: rawMatch.original?.license_plate ?? rawMatch.license_plate ?? rawMatch.label ?? strId,
          companyName: rawMatch.companyName ?? 'Unknown Company',
          raw: rawMatch.raw ?? rawMatch
        };
      }
      return { value: strId, label: strId, companyName: 'Unknown Company', raw: null };
    });
    setSelectedVehicles(newSelected);
  };

  const parseTimeToSeconds = (timeStr) => {
    if (!timeStr || typeof timeStr !== 'string') return 0;
    const parts = timeStr.split(':').map(Number);
    if (parts.length === 3) {
      return parts[0] * 3600 + parts[1] * 60 + parts[2];
    } else if (parts.length === 2) {
      return parts[0] * 60 + parts[1];
    }
    return 0;
  };

  const formatSecondsToTime = (totalSec) => {
    const hrs = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const formatCurrency = (v) =>
    new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', maximumFractionDigits: 0 }).format(Number(v || 0));

  const getUniqueFuelPriceInfo = (rawList) => {
    if (!rawList) return [];
    const list = Array.isArray(rawList) ? rawList : [rawList];
    const unique = [];
    const seen = new Set();
    list.forEach((item) => {
      if (!item || typeof item !== 'object') return;
      const price = item.price != null ? item.price : item.fuel_price;
      const name = item.fuel_type_name || item.name || '';
      const code = item.fuel_type_code || item.code || '';
      const validAt = item.valid_at || item.validAt || '';
      const key = `${price}_${name}_${code}_${validAt}`;

      if (!seen.has(key) && (price != null || name || code)) {
        seen.add(key);
        unique.push({ price, name, code, validAt });
      }
    });
    return unique;
  };

  const fetchData = useCallback(async () => {
    if (!fromDate || !toDate) {
      notify({ message: t('vehicleFuelSummary.errSelectDateRange', 'Please select a date range first'), severity: 'warning' });
      return;
    }
    const localStart = dayjs(fromDate).second(0);
    const localEnd = dayjs(toDate).second(59);

    if (!validateDateRange(localStart, localEnd)) {
      notify({
        message: dateError || t('vehicleFuelSummary.errInvalidDateRange', 'Invalid date range'),
        severity: 'warning'
      });
      return;
    }

    const targetIds = selectedVehicleIds.length > 0 ? selectedVehicleIds : vehicleList.map((v) => String(v.value));
    if (targetIds.length === 0) {
      notify({ message: t('vehicleFuelSummary.errSelectVehicle', 'Please select at least one vehicle'), severity: 'warning' });
      return;
    }

    setLoading(true);
    setLoadingProgress(0);
    setLoadingText('Starting data fetching process...');
    setRows([]);
    setPage(0);

    try {
      let apiChunks = [];
      const pType = periodType;
      const isFullDayPeriod = ['week', 'last_week', 'month', 'last_month'].includes(pType);

      if (isFullDayPeriod) {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');
        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          apiChunks.push({
            startLocal: curr.startOf('day'),
            endLocal: curr.endOf('day'),
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });
          curr = curr.add(1, 'day');
        }
      } else {
        let curr = localStart.startOf('day');
        const end = localEnd.startOf('day');

        while (curr.isBefore(end) || curr.isSame(end, 'day')) {
          let chunkStart = curr.startOf('day');
          let chunkEnd = curr.endOf('day');

          if (curr.isSame(localStart, 'day')) chunkStart = localStart;
          if (curr.isSame(localEnd, 'day')) chunkEnd = localEnd;

          apiChunks.push({
            startLocal: chunkStart,
            endLocal: chunkEnd,
            dateObj: curr,
            dateStr: curr.format('YYYY-MM-DD')
          });

          curr = curr.add(1, 'day');
        }
      }

      const aggregateVehicleFuel = (rows, vInfo) => {
        if (!rows || rows.length === 0) return null;

        const sumData = {
          vehicle_id: vInfo.value,
          license_plate: vInfo.label,
          plate: vInfo.label,
          type: vInfo.raw?.vehicle_type_name,
          vehicle_type_name: vInfo.raw?.vehicle_type_name,
          calculate_fuel: [],
          fuel_price_info: [],
          total_bbm_usage: 0,
          total_bbm_usage_canbus: 0,
          total_price_bbm_usage_canbus: 0,
          distance: 0,
          total_engine_on_sec: 0,
          idle_time_sec: 0
        };

        rows.forEach((r) => {
          if (Array.isArray(r.calculate_fuel)) sumData.calculate_fuel = sumData.calculate_fuel.concat(r.calculate_fuel);
          if (Array.isArray(r.fuel_price_info)) {
            sumData.fuel_price_info = sumData.fuel_price_info.concat(r.fuel_price_info);
          } else if (r.fuel_price_info) {
            sumData.fuel_price_info.push(r.fuel_price_info);
          }

          sumData.total_bbm_usage += Number(r.total_bbm_usage || 0);
          sumData.total_bbm_usage_canbus += Number(r.total_bbm_usage_canbus || 0);
          sumData.total_price_bbm_usage_canbus += Number(r.total_price_bbm_usage_canbus ?? r.total_price_bbm_usage ?? 0);
          sumData.distance += Number(r.distance || 0);
          sumData.total_engine_on_sec += parseTimeToSeconds(r.total_engine_on);
          sumData.idle_time_sec += parseTimeToSeconds(r.idle_time);
        });

        sumData.total_engine_on = formatSecondsToTime(sumData.total_engine_on_sec);
        sumData.idle_time = formatSecondsToTime(sumData.idle_time_sec);
        sumData.km_to_litre = sumData.total_bbm_usage > 0 ? sumData.distance / sumData.total_bbm_usage : 0;
        sumData.km_to_litre_canbus = sumData.total_bbm_usage_canbus > 0 ? sumData.distance / sumData.total_bbm_usage_canbus : 0;

        return sumData;
      };

      const totalVehicles = targetIds.length;
      let completedVehicles = 0;
      const chunkSize = 5;
      const accumulatedData = [];

      for (let i = 0; i < totalVehicles; i += chunkSize) {
        const chunk = targetIds.slice(i, i + chunkSize);
        const chunkResults = await Promise.all(
          chunk.map(async (id) => {
            const vInfo =
              selectedVehicles.find((v) => String(v.value) === String(id)) ||
              vehicleOptionsNormalized.find((v) => String(v.value) === String(id));
            const vCompanyId = vInfo?.raw?.company || vInfo?.raw?.join?.company_code || 0;
            const licensePlate = vInfo?.label || String(id);

            let vehicleDailyData = [];

            const tz = typeof timezone === 'function' ? timezone() : Number(timezone) || 7;
            for (const chunk of apiChunks) {
              const apiBeginTime = chunk.startLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');
              const apiEndTime = chunk.endLocal.subtract(tz, 'hour').format('YYYY-MM-DDTHH:mm:ss[Z]');

              const payload = {
                company_id: Number(vCompanyId),
                start_date: apiBeginTime,
                end_date: apiEndTime,
                vehicle_id: Number(id),
                tz
              };

              try {
                const res = await filterReportFuelSummary(payload, tz);
                const items = Array.isArray(res) ? res : res?.data || [];
                vehicleDailyData = vehicleDailyData.concat(items);
              } catch (e) {
                console.error(`Error fetching for ${licensePlate} on ${apiBeginTime}`, e);
              }
            }

            try {
              const aggregated = aggregateVehicleFuel(vehicleDailyData, vInfo);
              return aggregated ? [aggregated] : [];
            } finally {
              completedVehicles++;
              setLoadingProgress(Math.round((completedVehicles / totalVehicles) * 100));
              setLoadingText(`Processing unit: ${licensePlate} (${completedVehicles}/${totalVehicles})`);
            }
          })
        );
        accumulatedData.push(...chunkResults.flat());
      }

      setRows(accumulatedData);
    } catch (error) {
      console.error('Failed to fetch report data:', error);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [selectedVehicleIds, fromDate, toDate, notify, t, vehicleOptionsNormalized, selectedVehicles]);

  // Search filter logic by selected column / all
  const filteredRows = useMemo(() => {
    if (!tableSearchQuery.trim()) return rows;
    const q = tableSearchQuery.trim().toLowerCase();

    return rows.filter((row) => {
      const plate = (row.license_plate || row.plate || '').toLowerCase();
      const type = (row.vehicle_type_name || row.type || '').toLowerCase();

      const calculateFuel = Array.isArray(row.calculate_fuel) ? row.calculate_fuel : [];
      const totalRefuel = calculateFuel.reduce((acc, curr) => acc + (Number(curr.rest_fuel_level) || 0), 0);
      const refuelingVal = calculateFuel.length > 0 ? Number(totalRefuel).toFixed(2) : '0';

      const usage = Number(row.total_bbm_usage) || 0;
      const usageCanbus = Number(row.total_bbm_usage_canbus) || 0;
      const usageVal = (usage <= 0 ? usageCanbus : usage).toFixed(2);

      const distanceVal = Number(row.distance).toFixed(2) || '0';
      const durationVal = (row.total_engine_on || '00:00:00').toLowerCase();
      const idleVal = (row.idle_time || '00:00:00').toLowerCase();

      const eff = Number(row.km_to_litre) || 0;
      const effCanbus = Number(row.km_to_litre_canbus) || 0;
      const effVal = (eff <= 0 ? effCanbus : eff).toFixed(2);

      const costVal = Number(row.total_price_bbm_usage_canbus ?? row.total_price_bbm_usage ?? 0);
      const costFormatted = formatCurrency(costVal).toLowerCase();

      const fpList = getUniqueFuelPriceInfo(row.fuel_price_info);
      const fuelTypeStr = fpList
        .map((fp) => [fp.name, fp.code].filter(Boolean).join(', '))
        .join(' ')
        .toLowerCase();
      const fuelPriceStr = fpList
        .map((fp) => (fp.price != null ? 'Rp ' + Number(fp.price).toLocaleString('id-ID') : ''))
        .join(' ')
        .toLowerCase();
      const validAtStr = fpList
        .map((fp) => (fp.validAt && dayjs(fp.validAt).isValid() ? dayjs(fp.validAt).format('DD MMM YYYY') : ''))
        .join(' ')
        .toLowerCase();

      if (searchField === 'license_plate') return plate.includes(q);
      if (searchField === 'vehicle_type_name') return type.includes(q);
      if (searchField === 'refueling') return refuelingVal.includes(q);
      if (searchField === 'total_bbm_usage') return usageVal.includes(q);
      if (searchField === 'distance') return distanceVal.includes(q) || String(row.distance || '').includes(q);
      if (searchField === 'total_engine_on') return durationVal.includes(q);
      if (searchField === 'idle_time') return idleVal.includes(q);
      if (searchField === 'efficiency') return effVal.includes(q);
      if (searchField === 'fuel_type') return fuelTypeStr.includes(q);
      if (searchField === 'fuel_price') return fuelPriceStr.includes(q) || fpList.some((fp) => String(fp.price || '').includes(q));
      if (searchField === 'valid_at') return validAtStr.includes(q);
      if (searchField === 'cost') return costFormatted.includes(q) || String(costVal).includes(q);

      // Default 'all': check across all columns
      return (
        plate.includes(q) ||
        type.includes(q) ||
        refuelingVal.includes(q) ||
        usageVal.includes(q) ||
        distanceVal.includes(q) ||
        String(row.distance || '').includes(q) ||
        durationVal.includes(q) ||
        idleVal.includes(q) ||
        effVal.includes(q) ||
        fuelTypeStr.includes(q) ||
        fuelPriceStr.includes(q) ||
        validAtStr.includes(q) ||
        costFormatted.includes(q) ||
        String(costVal).includes(q)
      );
    });
  }, [rows, tableSearchQuery, searchField]);

  // Paginated rows for custom table
  const paginatedRows = useMemo(() => {
    return filteredRows.slice(page * rowsPerPage, (page + 1) * rowsPerPage);
  }, [filteredRows, page, rowsPerPage]);

  const [exportAnchorEl, setExportAnchorEl] = useState(null);
  const isExportMenuOpen = Boolean(exportAnchorEl);

  const handleExportClick = (event) => setExportAnchorEl(event.currentTarget);
  const handleExportClose = () => setExportAnchorEl(null);

  const getFormattedDate = () => dayjs().format('YYYYMMDD-HHmmss');

  const exportToExcel = async () => {
    handleExportClose();
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Vehicle Fuel Summary');

      const cols = [
        { header: t('vehicleFuelSummary.plateNumber', 'License Plate'), key: 'license_plate', width: 18 },
        { header: t('vehicleFuelSummary.vehicleType', 'Vehicle Type'), key: 'vehicle_type_name', width: 20 },
        { header: 'Est Refueling (L)', key: 'refueling', width: 18 },
        { header: 'Fuel Consumption (L)', key: 'total_bbm_usage', width: 22 },
        { header: 'Distance (KM)', key: 'distance', width: 18 },
        { header: 'Duration', key: 'total_engine_on', width: 15 },
        { header: 'Idle Time', key: 'idle_time', width: 15 },
        { header: 'Efficiency (KM/L)', key: 'efficiency', width: 18 },
        { header: 'Fuel Type', key: 'fuel_type', width: 25 },
        { header: 'Fuel Price', key: 'fuel_price', width: 18 },
        { header: 'Valid At', key: 'valid_at', width: 18 },
        { header: t('vehicleFuelSummary.fuelCost', 'Fuel Cost (IDR)'), key: 'cost', width: 22 }
      ];

      worksheet.columns = cols;

      let currentExcelRow = 2; // Row 1 is header
      filteredRows.forEach((row) => {
        const calculateFuel = Array.isArray(row.calculate_fuel) ? row.calculate_fuel : [];
        const totalRefuel = calculateFuel.reduce((acc, curr) => acc + (Number(curr.rest_fuel_level) || 0), 0);
        const refuelingVal = calculateFuel.length > 0 ? Number(totalRefuel).toFixed(2) : '0';

        const usage = Number(row.total_bbm_usage) || 0;
        const usageCanbus = Number(row.total_bbm_usage_canbus) || 0;
        const usageVal = (usage <= 0 ? usageCanbus : usage).toFixed(2);

        const eff = Number(row.km_to_litre) || 0;
        const effCanbus = Number(row.km_to_litre_canbus) || 0;
        const effVal = (eff <= 0 ? effCanbus : eff).toFixed(2);

        const costVal = Number(row.total_price_bbm_usage_canbus ?? row.total_price_bbm_usage ?? 0);
        const costFormatted = formatCurrency(costVal);

        const fpList = getUniqueFuelPriceInfo(row.fuel_price_info);
        const displayFpList = fpList.length > 0 ? fpList : [{ price: null, name: '', code: '', validAt: '' }];
        const maxSubRows = displayFpList.length;

        displayFpList.forEach((fp, idx) => {
          const typeCodeStr = [fp.name, fp.code].filter(Boolean).join(', ') || '-';
          const formattedPrice = fp.price != null && fp.price !== '' ? 'Rp ' + Number(fp.price).toLocaleString('id-ID') : '-';
          const validAtStr = fp.validAt && dayjs(fp.validAt).isValid() ? dayjs(fp.validAt).format('DD MMM YYYY') : '-';

          worksheet.addRow({
            license_plate: row.license_plate || row.plate || '-',
            vehicle_type_name: row.vehicle_type_name || row.type || '-',
            refueling: refuelingVal,
            total_bbm_usage: usageVal,
            distance: Number(row.distance).toFixed(2) || '0',
            total_engine_on: row.total_engine_on || '00:00:00',
            idle_time: row.idle_time || '00:00:00',
            efficiency: effVal,
            fuel_type: typeCodeStr,
            fuel_price: formattedPrice,
            valid_at: validAtStr,
            cost: costFormatted
          });
        });

        if (maxSubRows > 1) {
          const mergeColKeys = [
            'license_plate',
            'vehicle_type_name',
            'refueling',
            'total_bbm_usage',
            'distance',
            'total_engine_on',
            'idle_time',
            'efficiency',
            'cost'
          ];
          cols.forEach((col, colIdx) => {
            if (mergeColKeys.includes(col.key)) {
              const colLetter = worksheet.getColumn(colIdx + 1).letter;
              const startCell = `${colLetter}${currentExcelRow}`;
              const endCell = `${colLetter}${currentExcelRow + maxSubRows - 1}`;
              worksheet.mergeCells(`${startCell}:${endCell}`);
              const cell = worksheet.getCell(startCell);
              cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
            }
          });
        }

        currentExcelRow += maxSubRows;
      });

      // Style Header Row (MUI Blue #5166AE background, Bold White text, Left Aligned)
      const headerRow = worksheet.getRow(1);
      headerRow.height = 24;
      headerRow.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11 };
      headerRow.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: 'FF5166AE' }
      };
      headerRow.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };

      // Add borders and force left alignment on all data cells
      worksheet.eachRow((row, rowNumber) => {
        row.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FFD0D7DE' } },
            left: { style: 'thin', color: { argb: 'FFD0D7DE' } },
            bottom: { style: 'thin', color: { argb: 'FFD0D7DE' } },
            right: { style: 'thin', color: { argb: 'FFD0D7DE' } }
          };
          if (rowNumber > 1) {
            cell.alignment = { vertical: 'middle', horizontal: 'left', wrapText: true };
          }
        });
      });

      // Freeze header view
      worksheet.views = [{ state: 'frozen', xSplit: 0, ySplit: 1 }];

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vehicle-fuel-summary-${getFormattedDate()}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export Excel:', err);
    }
  };

  const exportToCSV = async () => {
    handleExportClose();
    try {
      const formattedData = [];
      filteredRows.forEach((row) => {
        const calculateFuel = Array.isArray(row.calculate_fuel) ? row.calculate_fuel : [];
        const totalRefuel = calculateFuel.reduce((acc, curr) => acc + (Number(curr.rest_fuel_level) || 0), 0);
        const refuelingVal = calculateFuel.length > 0 ? Number(totalRefuel).toFixed(2) : '0';

        const usage = Number(row.total_bbm_usage) || 0;
        const usageCanbus = Number(row.total_bbm_usage_canbus) || 0;
        const usageVal = (usage <= 0 ? usageCanbus : usage).toFixed(2);

        const eff = Number(row.km_to_litre) || 0;
        const effCanbus = Number(row.km_to_litre_canbus) || 0;
        const effVal = (eff <= 0 ? effCanbus : eff).toFixed(2);

        const costVal = Number(row.total_price_bbm_usage_canbus ?? row.total_price_bbm_usage ?? 0);
        const costFormatted = formatCurrency(costVal);

        const fpList = getUniqueFuelPriceInfo(row.fuel_price_info);
        const displayFpList = fpList.length > 0 ? fpList : [{ price: null, name: '', code: '', validAt: '' }];

        displayFpList.forEach((fp, idx) => {
          const typeCodeStr = [fp.name, fp.code].filter(Boolean).join(', ') || '-';
          const formattedPrice = fp.price != null && fp.price !== '' ? 'Rp ' + Number(fp.price).toLocaleString('id-ID') : '-';
          const validAtStr = fp.validAt && dayjs(fp.validAt).isValid() ? dayjs(fp.validAt).format('DD MMM YYYY') : '-';

          formattedData.push({
            [t('vehicleFuelSummary.plateNumber', 'License Plate')]: idx === 0 ? row.license_plate || row.plate || '-' : '',
            [t('vehicleFuelSummary.vehicleType', 'Vehicle Type')]: idx === 0 ? row.vehicle_type_name || row.type || '-' : '',
            'Est Refueling (L)': idx === 0 ? refuelingVal : '',
            'Fuel Consumption (L)': idx === 0 ? usageVal : '',
            'Distance (KM)': idx === 0 ? Number(row.distance).toFixed(2) || '0' : '',
            Duration: idx === 0 ? row.total_engine_on || '00:00:00' : '',
            'Idle Time': idx === 0 ? row.idle_time || '00:00:00' : '',
            'Efficiency (KM/L)': idx === 0 ? effVal : '',
            'Fuel Type': typeCodeStr,
            'Fuel Price': formattedPrice,
            'Valid At': validAtStr,
            [t('vehicleFuelSummary.fuelCost', 'Fuel Cost (IDR)')]: idx === 0 ? costFormatted : ''
          });
        });
      });

      const csvData = Papa.unparse(formattedData);
      const blob = new Blob(['\uFEFF' + csvData], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vehicle-fuel-summary-${getFormattedDate()}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Failed to export CSV:', err);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Box sx={{ width: '100%', pb: 5 }}>
        {/* Top Filter Card */}
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            mb: 1,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(to right bottom, #ffffff, #fcfcfc)'
          }}
        >
          <Box
            sx={{
              p: 1,
              px: 2,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 2,
              bgcolor: 'rgba(25, 118, 210, 0.04)',
              borderRadius: 2
            }}
          >
            <Typography variant="body2" color="#1976d2" fontWeight="500" sx={{ lineHeight: 1.5 }}>
              {t('reports.fuelSummaryDesc', 'Fuel volume during estimated refueling, reduction, usage, fuel efficiency, and total cost.')}
            </Typography>
            {(localStorage.getItem('rolename') === 'Administrator' ||
              (() => {
                try {
                  return JSON.stringify(JSON.parse(localStorage.getItem('master_menu') || '[]')).includes(
                    '"modules_path":"report-catalog"'
                  );
                } catch {
                  return false;
                }
              })()) && (
              <Box>
                <Button
                  startIcon={<ArrowBackIcon fontSize="small" />}
                  onClick={() => navigate('/report-catalog?name=report-catalog')}
                  size="small"
                  variant="contained"
                  sx={{
                    bgcolor: 'primary',
                    color: '#ffffff',
                    textTransform: 'none',
                    px: 0.5,
                    py: 0,
                    minWidth: 0,
                    '&:hover': { bgcolor: '#36477c', color: '#ffffff' }
                  }}
                >
                  Report Catalog
                </Button>
              </Box>
            )}
          </Box>
          <Box display="flex" flexWrap="wrap" gap={2.5} alignItems="center">
            <Box sx={{ flex: '1 1 200px' }}>
              <PickerField
                label="Company"
                value={selectedCompanyIds}
                {...companyPickerParams}
                options={companyOptions}
                onChange={(ids) => {
                  setSelectedCompanyIds(ids || []);
                  setSelectedVehicles([]);
                }}
                dialogTitle="Select Company"
                multiSelect={true}
              />
            </Box>
            <Box sx={{ flex: '1 1 250px' }}>
              <PickerField
                label={t('general.vehicle', 'Select Vehicle')}
                value={selectedVehicleIds}
                selectedItems={selectedVehicles}
                {...vehiclePickerParams}
                options={vehicleOptionsNormalized}
                onChange={handleVehicleChange}
                dialogTitle={t('general.vehicle', 'Select Vehicle')}
                multiSelect={true}
              />
            </Box>
            <Box sx={{ flex: '0 1 180px' }}>
              <FormControl fullWidth size="medium">
                <InputLabel id="period-label">{t('vehicleFuelSummary.selectPeriod', 'Select Period')}</InputLabel>
                <MuiSelect
                  labelId="period-label"
                  value={periodType}
                  onChange={(e) => setPeriodType(e.target.value)}
                  disabled={loading}
                  input={<OutlinedInput label={t('vehicleFuelSummary.selectPeriod', 'Select Period')} />}
                  sx={{ bgcolor: 'background.paper' }}
                >
                  <MenuItem value="today">Today</MenuItem>
                  <MenuItem value="week">{t('vehicleFuelSummary.lastWeek', 'Last Week')}</MenuItem>
                  <MenuItem value="month">{t('vehicleFuelSummary.lastMonth', 'Last Month')}</MenuItem>
                  <MenuItem value="custom">{t('vehicleFuelSummary.custom', 'Custom')}</MenuItem>
                </MuiSelect>
              </FormControl>
            </Box>

            {periodType === 'custom' && (
              <Box sx={{ flex: '1 1 350px' }}>
                <Stack direction="row" spacing={2}>
                  <DateTimePicker
                    ampm={false}
                    label={t('vehicleFuelSummary.from', 'From')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={fromDate ? dayjs(fromDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(0).format('YYYY-MM-DD HH:mm:ss') : '';
                      setFromDate(newV);
                      validateDateRange(dayjs(newV), dayjs(toDate));
                    }}
                    maxDate={toDate ? dayjs(toDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                  <DateTimePicker
                    ampm={false}
                    label={t('vehicleFuelSummary.to', 'To')}
                    timeSteps={{ minutes: 1 }}
                    format="DD/MM/YYYY HH:mm"
                    value={toDate ? dayjs(toDate) : null}
                    onChange={(v) => {
                      const newV = v ? v.second(59).format('YYYY-MM-DD HH:mm:ss') : '';
                      setToDate(newV);
                      validateDateRange(dayjs(fromDate), dayjs(newV));
                    }}
                    minDate={fromDate ? dayjs(fromDate) : undefined}
                    disabled={loading}
                    slotProps={{ textField: { size: 'small', fullWidth: true, error: !!dateError, sx: { bgcolor: 'background.paper' } } }}
                  />
                </Stack>
              </Box>
            )}

            <Box sx={{ display: 'flex', gap: 1.5, ml: 'auto', mt: { xs: 2, md: 0 } }}>
              <Button
                variant="outlined"
                color="inherit"
                disabled={loading}
                onClick={() => {
                  setSelectedCompanyIds(() => {
                    const defaultId = getCompanyIdFromStorage();
                    return defaultId ? [defaultId] : [];
                  });
                  setSelectedVehicles([]);
                  setTableSearchQuery('');
                  setSearchField('all');
                  setRows([]);
                  setPage(0);
                  setPeriodType('today');
                  setDateError('');
                }}
                sx={{ px: 2, minWidth: 0, bgcolor: '#ffffff' }}
              >
                <RestartAltIcon />
              </Button>
              <Button
                variant="contained"
                color="primary"
                startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <SearchIcon />}
                onClick={fetchData}
                disabled={loading || !!dateError || selectedVehicleIds.length === 0}
                sx={{ px: 3, fontWeight: 'bold', boxShadow: 2 }}
              >
                {loading ? t('buttonTitle.generating', 'Generating...') : t('buttonTitle.generate', 'Generate')}
              </Button>
            </Box>
          </Box>
          {dateError && (
            <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
              {dateError}
            </Typography>
          )}
        </Paper>

        {/* Date range & Top Search / Export bar - Sticky / FlexShrink 0 */}
        <Paper variant="outlined" sx={{ p: 1.5, mb: 1, flexShrink: 0, borderRadius: 2, bgcolor: '#ffffff' }}>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
            {rows.length > 0 ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <InfoOutlinedIcon sx={{ color: '#485D9A' }} />
                <Typography variant="body2" sx={{ color: '#333', fontWeight: 500 }}>
                  {t('revenue.showingDataFrom', 'Showing data from')}{' '}
                  <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                    {dayjs(fromDate).format('DD MMMM YYYY HH:mm:ss')}
                  </Box>{' '}
                  {t('revenue.to', 'to')}{' '}
                  <Box component="span" sx={{ color: '#485D9A', fontWeight: 'bold' }}>
                    {dayjs(toDate).format('DD MMMM YYYY HH:mm:ss')}
                  </Box>
                </Typography>
              </Box>
            ) : (
              <Box />
            )}

            {/* Top Search Controls (Search By + Input) + Export */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, ml: 'auto', flexWrap: 'wrap' }}>
              <FormControl size="small" sx={{ minWidth: 180 }}>
                <InputLabel id="search-by-label">Search By</InputLabel>
                <MuiSelect labelId="search-by-label" value={searchField} onChange={(e) => setSearchField(e.target.value)} label="Search By">
                  <MenuItem value="all">All Columns</MenuItem>
                  <MenuItem value="license_plate">Plate Number</MenuItem>
                  <MenuItem value="vehicle_type_name">Vehicle Type</MenuItem>
                  <MenuItem value="refueling">Est Refueling (L)</MenuItem>
                  <MenuItem value="total_bbm_usage">Fuel Consumption (L)</MenuItem>
                  <MenuItem value="distance">Distance (KM)</MenuItem>
                  <MenuItem value="total_engine_on">Duration</MenuItem>
                  <MenuItem value="idle_time">Idle Time</MenuItem>
                  <MenuItem value="efficiency">Efficiency (KM/L)</MenuItem>
                  <MenuItem value="fuel_type">Fuel Type</MenuItem>
                  <MenuItem value="fuel_price">Fuel Price</MenuItem>
                  <MenuItem value="valid_at">Valid At</MenuItem>
                  <MenuItem value="cost">Fuel Cost (IDR)</MenuItem>
                </MuiSelect>
              </FormControl>

              <TextField
                size="small"
                placeholder="Search data..."
                value={tableSearchQuery}
                onChange={(e) => {
                  setTableSearchQuery(e.target.value);
                  setPage(0);
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                    </InputAdornment>
                  )
                }}
                sx={{ minWidth: 220 }}
              />

              {rows.length > 0 && (
                <>
                  <Button
                    variant="outlined"
                    color="primary"
                    size="small"
                    startIcon={<DownloadIcon />}
                    endIcon={<ArrowDropDownIcon />}
                    onClick={handleExportClick}
                    sx={{ textTransform: 'none', fontWeight: 600, bgcolor: '#ffffff', py: 0.8 }}
                  >
                    Export
                  </Button>
                  <Menu anchorEl={exportAnchorEl} open={isExportMenuOpen} onClose={handleExportClose}>
                    <MenuItem onClick={exportToExcel}>{t('table.export.excel', 'Export to Excel')}</MenuItem>
                    <MenuItem onClick={exportToCSV}>{t('table.export.csv', 'Export to CSV')}</MenuItem>
                  </Menu>
                </>
              )}
            </Box>
          </Box>
        </Paper>

        {loading && loadingProgress > 0 && (
          <Box
            sx={{
              p: 4,
              flexShrink: 0,
              textAlign: 'center',
              bgcolor: '#fff',
              borderRadius: 2,
              mb: 1,
              border: '1px dashed rgba(72, 93, 154, 0.3)'
            }}
          >
            <Box sx={{ width: '60%', mx: 'auto', mt: 2, mb: 2, textAlign: 'center' }}>
              <LinearProgress
                variant="determinate"
                value={loadingProgress}
                sx={{
                  height: 10,
                  borderRadius: 5,
                  mb: 1.5,
                  bgcolor: 'rgba(72, 93, 154, 0.2)',
                  '& .MuiLinearProgress-bar': { bgcolor: '#485D9A' }
                }}
              />
              <Typography variant="body2" color="text.secondary" fontWeight="bold">
                {loadingProgress}% - {loadingText}
              </Typography>
            </Box>
          </Box>
        )}

        {/* Custom HTML Table Container */}
        <Paper variant="outlined" sx={{ width: '100%', borderRadius: 2, mt: 1, overflow: 'hidden' }}>
          <TableContainer sx={{ width: '100%', overflowX: 'auto', overflowY: 'auto', maxHeight: 'calc(100vh - 220px)', minHeight: 520 }}>
            <Table stickyHeader size="small" sx={{ minWidth: 1700, borderCollapse: 'collapse' }}>
              <TableHead>
                <TableRow
                  sx={{
                    '& th': {
                      bgcolor: '#5166AE',
                      fontWeight: 700,
                      color: '#ffffff',
                      fontSize: 13,
                      whiteSpace: 'nowrap',
                      py: 1.5,
                      px: 2,
                      borderBottom: '2px solid rgba(224, 224, 224, 0.2)',
                      position: 'sticky',
                      top: 0,
                      zIndex: 3
                    }
                  }}
                >
                  <TableCell sx={{ minWidth: 130 }}>{t('vehicleFuelSummary.plateNumber', 'License Plate')}</TableCell>
                  <TableCell sx={{ minWidth: 160 }}>{t('vehicleFuelSummary.vehicleType', 'Vehicle Type')}</TableCell>
                  <TableCell align="right" sx={{ minWidth: 140 }}>
                    Est Refueling (L)
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}>
                    Fuel Consumption (L)
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 150 }}>
                    Distance (KM)
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 130 }}>
                    Duration
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 130 }}>
                    Idle Time
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 140 }}>
                    Efficiency (KM/L)
                  </TableCell>
                  <TableCell sx={{ minWidth: 180 }}>Fuel Type</TableCell>
                  <TableCell align="right" sx={{ minWidth: 140 }}>
                    Fuel Price
                  </TableCell>
                  <TableCell align="center" sx={{ minWidth: 140 }}>
                    Valid At
                  </TableCell>
                  <TableCell align="right" sx={{ minWidth: 160 }}>
                    {t('vehicleFuelSummary.fuelCost', 'Fuel Cost (IDR)')}
                  </TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {paginatedRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={12} align="center" sx={{ py: 6 }}>
                      <Typography variant="body2" color="text.secondary">
                        {loading ? 'Loading data...' : 'No data found'}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  paginatedRows.map((row, rIdx) => {
                    const fpList = getUniqueFuelPriceInfo(row.fuel_price_info);
                    const displayFpList = fpList.length > 0 ? fpList : [{ price: null, name: '', code: '', validAt: '' }];
                    const rowSpan = displayFpList.length;

                    const calculateFuel = Array.isArray(row.calculate_fuel) ? row.calculate_fuel : [];
                    const totalRefuel = calculateFuel.reduce((acc, curr) => acc + (Number(curr.rest_fuel_level) || 0), 0);
                    const refuelingVal = calculateFuel.length > 0 ? Number(totalRefuel).toFixed(2) : '0';

                    const usage = Number(row.total_bbm_usage) || 0;
                    const usageCanbus = Number(row.total_bbm_usage_canbus) || 0;
                    const usageVal = (usage <= 0 ? usageCanbus : usage).toFixed(2);

                    const eff = Number(row.km_to_litre) || 0;
                    const effCanbus = Number(row.km_to_litre_canbus) || 0;
                    const effVal = (eff <= 0 ? effCanbus : eff).toFixed(2);

                    const costVal = Number(row.total_price_bbm_usage_canbus ?? row.total_price_bbm_usage ?? 0);
                    const costFormatted = formatCurrency(costVal);

                    return displayFpList.map((fp, subIdx) => {
                      const typeCodeStr = [fp.name, fp.code].filter(Boolean).join(', ') || '-';
                      const formattedPrice = fp.price != null && fp.price !== '' ? 'Rp ' + Number(fp.price).toLocaleString('id-ID') : '-';
                      const validAtStr =
                        fp.validAt &&
                        !String(fp.validAt).startsWith('0001-01-01') &&
                        !String(fp.validAt).startsWith('1900-01-01') &&
                        dayjs(fp.validAt).isValid()
                          ? dayjs(fp.validAt).format('DD MMM YYYY')
                          : '-';

                      return (
                        <TableRow
                          key={`${rIdx}_${subIdx}`}
                          hover
                          sx={{
                            bgcolor: rIdx % 2 === 1 ? 'rgba(248, 250, 252, 0.7)' : '#ffffff',
                            '& td': {
                              borderBottom: subIdx === rowSpan - 1 ? '2px solid #cbd5e1' : '1px solid #e2e8f0',
                              whiteSpace: 'nowrap',
                              px: 2,
                              py: 1.2,
                              paddingLeft: '16px !important'
                            }
                          }}
                        >
                          {subIdx === 0 && (
                            <>
                              <TableCell rowSpan={rowSpan} sx={{ fontWeight: 600, verticalAlign: 'middle' }}>
                                {row.license_plate || row.plate || '-'}
                              </TableCell>
                              <TableCell rowSpan={rowSpan} sx={{ verticalAlign: 'middle' }}>
                                {row.vehicle_type_name || row.type || '-'}
                              </TableCell>
                              <TableCell rowSpan={rowSpan} align="right" sx={{ verticalAlign: 'middle' }}>
                                {refuelingVal}
                              </TableCell>
                              <TableCell rowSpan={rowSpan} align="right" sx={{ verticalAlign: 'middle' }}>
                                {usageVal}
                              </TableCell>
                              <TableCell rowSpan={rowSpan} align="right" sx={{ verticalAlign: 'middle' }}>
                                {Number(row.distance).toFixed(2) || 0}
                              </TableCell>
                              <TableCell rowSpan={rowSpan} align="center" sx={{ verticalAlign: 'middle' }}>
                                {row.total_engine_on || '00:00:00'}
                              </TableCell>
                              <TableCell rowSpan={rowSpan} align="center" sx={{ verticalAlign: 'middle' }}>
                                {row.idle_time || '00:00:00'}
                              </TableCell>
                              <TableCell rowSpan={rowSpan} align="center" sx={{ verticalAlign: 'middle' }}>
                                {effVal}
                              </TableCell>
                            </>
                          )}
                          {/* Subrow cells for Fuel Type, Fuel Price, Valid At */}
                          <TableCell sx={{ verticalAlign: 'middle', bgcolor: 'rgba(25, 118, 210, 0.02)', paddingLeft: '16px !important' }}>
                            {typeCodeStr}
                          </TableCell>
                          <TableCell
                            align="right"
                            sx={{ verticalAlign: 'middle', bgcolor: 'rgba(25, 118, 210, 0.02)', fontWeight: 600, color: 'primary.main' }}
                          >
                            {formattedPrice}
                          </TableCell>
                          <TableCell
                            align="center"
                            sx={{ verticalAlign: 'middle', bgcolor: 'rgba(25, 118, 210, 0.02)', color: 'text.secondary', fontSize: 12 }}
                          >
                            {validAtStr}
                          </TableCell>
                          {subIdx === 0 && (
                            <TableCell rowSpan={rowSpan} align="right" sx={{ fontWeight: 700, verticalAlign: 'middle' }}>
                              {costFormatted}
                            </TableCell>
                          )}
                        </TableRow>
                      );
                    });
                  })
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            sx={{ flexShrink: 0, borderTop: '1px solid #e0e0e0' }}
            component="div"
            count={filteredRows.length}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            rowsPerPageOptions={[10, 25, 50, 100]}
          />
        </Paper>
      </Box>
    </LocalizationProvider>
  );
}

export default function VehicleFuelSummaryPage() {
  return <VehicleFuelSummaryPageContent />;
}
